/**
 * Wraps bare http(s) URLs in an HTML string with clickable anchor tags.
 *
 * Used for seller-authored content (product descriptions) that stores URLs as
 * plain text. Safe by construction:
 *  - Only `http://` / `https://` URLs are matched, so no `javascript:` or other
 *    scheme can be injected as an href.
 *  - The string is tokenized into tags vs. text; only TEXT outside an existing
 *    `<a>` element is linkified, so URLs inside attributes (e.g. `src="..."`) and
 *    URLs already wrapped in a link are left untouched (no nested anchors).
 *  - Which domains can appear here is already governed upstream by the messaging
 *    URL allowlist (redaction at save time), so this only makes already-trusted
 *    links clickable.
 */
// Match URL chars, but stop at HTML entity boundaries: a bare `&` ends the URL
// (so a trailing `&nbsp;` / `&quot;` isn't swallowed), while the encoded
// ampersand `&amp;` — common inside query strings — is kept as part of the URL.
const URL_RE = /https?:\/\/(?:&amp;|[^\s<>"'`&])+/g;
const ANCHOR_CLASS = 'underline break-words text-brand-secondary';

// MIRROR of the messaging URL allowlist in
// src/utils/firestore/messages/contact-info-detection.ts — KEEP IN SYNC.
// Only allowlisted hosts get auto-linked, so legacy/integration-sourced
// descriptions that never went through save-time redaction can't produce a
// clickable link to a non-allowlisted (e.g. phishing) domain.
const ALLOWLIST_SUBDOMAIN_HOSTS = [
  'gearfocus.com',
  'usps.com',
  'ups.com',
  'fedex.com',
  'youtube.com',
  'youtu.be',
  'bluemark.io',
  'dronescout.co',
];
const ALLOWLIST_EXACT_HOSTS = ['gearfocus.b-cdn.net'];

function isAllowlistedUrl(urlStr: string): boolean {
  try {
    const url = new URL(urlStr);
    const host = url.hostname.toLowerCase();
    const hostAllowed =
      ALLOWLIST_EXACT_HOSTS.includes(host) ||
      ALLOWLIST_SUBDOMAIN_HOSTS.some(
        (allowed) => host === allowed || host.endsWith('.' + allowed)
      );
    if (!hostAllowed) return false;
    // Don't link open-redirect / embedded-URL smuggling through an allowed host.
    const rest = (url.pathname + url.search).toLowerCase();
    return !(
      rest.includes('//') ||
      /https?:/.test(rest) ||
      rest.includes('%2f%2f') ||
      rest.includes('http%3a')
    );
  } catch {
    return false;
  }
}

export function linkifyHtml(html: string): string {
  if (!html) return html;

  // Odd-indexed tokens are tags ("<...>"), even-indexed are text between tags.
  const tokens = html.split(/(<[^>]+>)/g);
  let anchorDepth = 0;

  return tokens
    .map((tok) => {
      if (tok.startsWith('<') && tok.endsWith('>')) {
        const a = /^<\s*(\/?)\s*a(?:\s|>|\/)/i.exec(tok);
        if (a) {
          if (a[1]) anchorDepth = Math.max(0, anchorDepth - 1);
          else if (!tok.endsWith('/>')) anchorDepth += 1;
        }
        return tok;
      }
      if (anchorDepth > 0) return tok; // inside an existing <a> — don't double-link
      return tok.replace(URL_RE, (url) => {
        // Don't pull trailing sentence punctuation into the link target.
        const trail = /[.,;:!?)\]]+$/.exec(url)?.[0] ?? '';
        const href = trail ? url.slice(0, -trail.length) : url;
        if (!href || !isAllowlistedUrl(href)) return url; // only link trusted hosts
        const safeHref = href.replace(/"/g, '%22');
        return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="${ANCHOR_CLASS}">${href}</a>${trail}`;
      });
    })
    .join('');
}

/**
 * Linkifies PLAIN TEXT (no HTML). Escapes HTML special chars first so the
 * result is safe to render via dangerouslySetInnerHTML, then auto-links URLs.
 * Use for the plain-text description fallback (listings without description_html).
 */
export function linkifyPlainText(text: string): string {
  if (!text) return text;
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return linkifyHtml(escaped);
}
